import { isTerminalStatus, type JobStepId, type JobView } from '@detachly/contracts'
import { useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArtifactRow } from '../components/ArtifactRow'
import { Card, ErrorPanel, Skeleton, StatusBadge } from '../components/ui'
import { useJobPolling } from '../hooks/useJobPolling'
import { useNow } from '../hooks/useNow'
import { copyForCode } from '../lib/errors'
import { formatDateTime, formatElapsed, jobLabel } from '../lib/format'

const STEP_LABELS: Record<JobStepId, string> = {
  extract: 'Read the Framer project',
  ir: 'Build the project model',
  codegen: 'Generate files',
  verify: 'Verify the result',
  package: 'Package',
  upload: 'Upload',
}

const STEP_MARKS: Record<string, string> = {
  done: '✓',
  running: '•',
  failed: '!',
  skipped: '–',
  pending: '',
}

function StepList({ job }: { job: JobView }) {
  if (job.progress.steps.length === 0) {
    return <p className="muted">Waiting for a runner to pick this up…</p>
  }

  return (
    <ol className="steps">
      {job.progress.steps.map((step) => (
        <li key={step.id} className={`step step--${step.state}`}>
          <span className="step__mark" aria-hidden="true">
            {STEP_MARKS[step.state] ?? ''}
          </span>
          <span className="step__copy">
            <span className="step__name">{STEP_LABELS[step.id]}</span>
            {step.detail ? <span className="step__detail">{step.detail}</span> : null}
            {!step.detail &&
            job.type === 'ai_home' &&
            step.id === 'codegen' &&
            step.state === 'running' ? (
              <span className="step__detail">
                Writing each section with the model — this step often takes 5–20 minutes and the
                screen will sit here while it does.
              </span>
            ) : null}
          </span>
          <span className="step__state">{step.state}</span>
        </li>
      ))}
    </ol>
  )
}

export function JobPage() {
  const { jobId = '' } = useParams()
  const { job, error, loading, polling, retry } = useJobPolling(jobId)
  const inFlight = job !== null && !isTerminalStatus(job.status)
  const now = useNow(inFlight)
  const elapsed = job ? formatElapsed(job.createdAt, now) : ''

  useEffect(() => {
    const previous = document.title
    return () => {
      document.title = previous
    }
  }, [])

  useEffect(() => {
    if (!job || !inFlight) return
    document.title = `${jobLabel(job.type)} · ${elapsed}`
  }, [job, inFlight, elapsed])

  if (loading) return <Skeleton rows={4} />
  // Once there is a job on screen, a dropped poll is a banner, not a replacement for the page.
  if (error && !job) return <ErrorPanel error={error} onRetry={retry} />
  if (!job) return null

  // A job-level failure arrives as a code inside the view, not as a thrown request error.
  const failure = job.error ? copyForCode(job.error.code) : null
  // A red build still writes a report, and that is exactly when it is worth reading.
  const hasReport = job.artifacts.some((artifact) => artifact.kind === 'report_md')

  return (
    <>
      <div className="page-head">
        <div className="page-head__text">
          <h1>{jobLabel(job.type)}</h1>
          <p className="muted">
            Started {formatDateTime(job.createdAt)}
            {job.finishedAt
              ? ` · finished ${formatDateTime(job.finishedAt)}`
              : ` · ${elapsed} elapsed`}
          </p>
        </div>
        <StatusBadge status={job.status} />
        <Link to={`/app/projects/${job.projectId}`} className="button button--secondary">
          Back to project
        </Link>
      </div>

      <div className="stack">
        {error ? <ErrorPanel error={error} onRetry={retry} /> : null}

        <Card>
          <div className="card__title">
            <h2>Progress</h2>
            {polling ? <span className="subtle">updating live</span> : null}
          </div>
          {job.type === 'ai_home' && inFlight ? (
            <p className="muted">
              Usually 5–20 minutes. Safe to leave this page — we email you when it finishes.
            </p>
          ) : null}
          <StepList job={job} />
        </Card>

        {job.status === 'failed' && job.error ? (
          <div className="notice notice--error" role="alert">
            <p className="notice__title">{failure?.title ?? 'This job failed.'}</p>
            <p>{failure?.action}</p>
            <p className="subtle mono">
              {job.error.code}: {job.error.message}
            </p>
            {hasReport ? (
              <div>
                <Link to={`/app/jobs/${job.id}/report`} className="button button--secondary">
                  Read how far it got
                </Link>
              </div>
            ) : null}
          </div>
        ) : null}

        {job.status === 'succeeded' ? (
          <Card>
            <div className="card__title">
              <h2>Files</h2>
              {hasReport ? (
                <Link to={`/app/jobs/${job.id}/report`} className="button button--secondary">
                  Read the report
                </Link>
              ) : null}
            </div>
            {job.artifacts.length === 0 ? (
              <p className="muted">
                This job produced no download of its own. A scan updates the project inventory —
                open the project to build a Content Pack from it.
              </p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">File</th>
                    <th scope="col">From</th>
                    <th scope="col">Size</th>
                    <th scope="col">Available</th>
                    <th scope="col" className="table__actions">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {job.artifacts.map((artifact) => (
                    <ArtifactRow key={artifact.id} artifact={artifact} job={job} />
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        ) : null}
      </div>
    </>
  )
}
